/**
 * Block 12 PR-B Phase B3 Part C Step 15 — ResultRow.
 *
 * Reusable row component для секций Б/В/Г/Д/Е/Ж AnketaForm. Renders:
 *   - row name (heading);
 *   - cost summary («Стоимость: N ₸»);
 *   - qty NumberInput с unit-suffix + ↻ кнопка reset-to-auto если row.qtyOverridden;
 *   - price NumberInput с ₸ suffix;
 *   - опциональная жёлтая deeplink-кнопка «Найти и узнать цену» (единый лейбл,
 *     PR-calc-buttons-unify, Aibek 2026-06-17) — caller передаёт `onDeeplink`
 *     который делает forcePersist + navigate (ADR-0020 §Q11, ADR-0099).
 *
 * Pure presenter — все handlers controlled через props. Editable mode зависит
 * от наличия `onQtyChange` / `onPriceChange` (read-only иначе).
 *
 * Reference: ADR-0020 §Q4 (override pattern qty/qtyAuto/qtyOverridden + ↻),
 * §Q11 (deeplink + forcePersist), B1 frozen NumberInput.
 */

import { RotateCcw } from 'lucide-react';
import { NumberInput } from '@/components/forms/NumberInput';
import type { ResultRowData } from '../types';

/**
 * Unified label per Aibek decision 2026-06-17 (ADR-0099) — все row-deeplink
 * кнопки показывают «Найти и узнать цену». Smart routing
 * (decor/partner/furniture/handles) скрыт от пользователя; target определяется
 * типом `row.deeplink`.
 */
const DEEPLINK_LABELS: Record<NonNullable<ResultRowData['deeplink']>, string> = {
  decor: 'Найти и узнать цену',
  partner: 'Найти и узнать цену',
  furniture: 'Найти и узнать цену',
  handles: 'Найти и узнать цену',
};

export interface ResultRowProps {
  row: ResultRowData;
  /** Если undefined — qty input read-only. */
  onQtyChange?: (qty: number) => void;
  /** ↻ reset-to-auto handler. Если undefined — ↻ кнопка скрыта даже при qtyOverridden. */
  onResetAuto?: () => void;
  /** Если undefined — price input read-only. */
  onPriceChange?: (price: number) => void;
  /**
   * Async deeplink handler. Caller обязан вызвать forcePersist() + navigate(...)
   * с returnTo URL — ResultRow не знает router contextually.
   */
  onDeeplink?: () => void | Promise<void>;
  qtyError?: string;
  priceError?: string;
  qtyHint?: string;
  /** Min/max для qty input (per row semantics — обычно 0 + Infinity). */
  qtyMin?: number;
  qtyMax?: number;
  /**
   * PR-H1 Round 2 (ADR-0026) — brand для material/furniture rows. Render
   * brand input при наличии `onBrandChange` (caller decides — keeps ResultRow
   * pure). Caller (SectionMaterials) пробрасывает handler для всех material
   * rows; secondary callers (Lighting/Services) не пробрасывают → input скрыт.
   */
  brand?: string;
  onBrandChange?: (brand: string) => void;
}

export function ResultRow({
  row,
  onQtyChange,
  onResetAuto,
  onPriceChange,
  onDeeplink,
  qtyError,
  priceError,
  qtyHint,
  qtyMin = 0,
  qtyMax,
  brand,
  onBrandChange,
}: ResultRowProps) {
  const qtyEditable = typeof onQtyChange === 'function';
  const priceEditable = typeof onPriceChange === 'function';
  const showResetButton = row.qtyOverridden && typeof onResetAuto === 'function';
  const deeplinkLabel = row.deeplink ? DEEPLINK_LABELS[row.deeplink] : undefined;
  const showDeeplink = deeplinkLabel !== undefined && typeof onDeeplink === 'function';
  const showBrandInput = typeof onBrandChange === 'function';

  return (
    <div
      className="rounded-2xl bg-card border border-accent p-4 flex flex-col gap-3"
      data-testid={`result-row-${row.id}`}
    >
      <div className="flex justify-between items-start gap-3">
        <h4 className="text-ink font-semibold text-base">{row.name}</h4>
        <div className="text-ink font-bold text-base whitespace-nowrap" aria-live="polite">
          {row.cost.toLocaleString('ru-RU')} ₸
        </div>
      </div>

      {row.adjustmentsDetail ? (
        <p
          className="text-xs text-muted italic -mt-1"
          data-testid={`result-row-${row.id}-adjustments`}
        >
          {row.adjustmentsDetail}
        </p>
      ) : null}

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <NumberInput
            name={`${row.id}-qty`}
            label={`Количество${row.qtyOverridden ? ' (вручную)' : ''}`}
            value={row.qty}
            onChange={(v) => qtyEditable && onQtyChange?.(v ?? 0)}
            unit={row.unit}
            min={qtyMin}
            max={qtyMax}
            error={qtyError}
            hint={qtyHint}
            disabled={!qtyEditable}
          />
        </div>
        {showResetButton ? (
          <button
            type="button"
            onClick={onResetAuto}
            aria-label={`Сбросить к авто (${row.qtyAuto} ${row.unit})`}
            data-testid={`result-row-${row.id}-reset-auto`}
            className="min-h-tap min-w-tap rounded-xl border-[1.5px] border-accent bg-card px-3 text-muted font-bold text-base focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          >
            <RotateCcw size={16} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <NumberInput
            name={`${row.id}-price`}
            label="Цена за единицу"
            value={row.price}
            onChange={(v) => priceEditable && onPriceChange?.(v ?? 0)}
            unit="₸"
            min={0}
            error={priceError}
            disabled={!priceEditable}
          />
        </div>
      </div>

      {showBrandInput ? (
        <div>
          <label htmlFor={`${row.id}-brand`} className="block text-sm font-medium text-ink mb-1">
            Бренд
          </label>
          <input
            id={`${row.id}-brand`}
            type="text"
            name={`${row.id}-brand`}
            aria-label={`Бренд для ${row.name}`}
            placeholder="Бренд производителя"
            value={brand ?? ''}
            onChange={(e) => onBrandChange?.(e.target.value)}
            maxLength={100}
            className="w-full min-h-tap rounded-xl border-[1.5px] border-accent bg-card px-3 text-ink text-base placeholder:text-muted/60 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
            data-testid={`result-row-${row.id}-brand`}
          />
        </div>
      ) : null}

      {showDeeplink ? (
        <button
          type="button"
          onClick={() => {
            // void: handler returns Promise (forcePersist + navigate) — caller
            // обязан handle errors. ResultRow только триггерит.
            void onDeeplink?.();
          }}
          className="min-h-tap rounded-xl bg-cta text-on-cta font-semibold text-base px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
        >
          {deeplinkLabel}
        </button>
      ) : null}
    </div>
  );
}
